import { useTranslation } from 'react-i18next';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import TableActionIcon from '@/components/ui/ActionIcon/TableActionIcon';
import OpenInquiryButton from '@/components/Inquiries/OpenInquiryButton';
import { InquiryResourceField } from '@/models/common/InquiryResourceField';
import Transaction from '@/models/Transaction';

interface TransactionRowActionsProps {
  transaction: Transaction;
  onDownloadReceipt: (transaction: Transaction) => void;
  onSendReceiptEmail: (transaction: Transaction) => void;
}

const kindsWithReceipt = ['תרומה', 'פיקדון', 'הלוואה'];

/** The row's action icons: receipt download, receipt by email and opening an inquiry. */
const TransactionRowActions = ({ transaction, onDownloadReceipt, onSendReceiptEmail }: TransactionRowActionsProps) => {
  const { t } = useTranslation();
  // Kinds that never have a receipt keep the space, so the inquiry icon stays aligned across all rows
  const isReceiptRelevant = kindsWithReceipt.includes(transaction.kind);
  const isReceiptMissing = !transaction.receiptId;

  /*
   * What the office needs to answer a question about this transaction, captured from the row
   * being clicked. Before this the inquiry carried only the transaction id, and a secretary
   * had to open the transactions board in another tab for every single inquiry.
   *
   * Everything here is already loaded, so it costs no request. Empty fields are dropped rather
   * than shown blank - a currency conversion or a reference is not always there - and the API
   * drops them again on its side.
   */
  const resourceSummary: InquiryResourceField[] = [
    { label: t('date'), value: new Date(transaction.date).toLocaleDateString('he-IL') },
    {
      label: t('amount'),
      value: transaction.amount.toLocaleString('he-IL', {
        style: 'currency',
        currency: transaction.currency || 'ILS',
        maximumFractionDigits: 2,
      }),
    },
    { label: t('kind'), value: transaction.kind },
    /*
     * ⚠️ t(), not the raw value. paymentMethod is a translation KEY, not a label - the board's
     * own cell renders it as t(value) - and passing it through unchanged put "creditCard" in
     * front of the office in the details box and in the notification email. Caught in a
     * screenshot, not by any test.
     */
    { label: t('paymentMethod'), value: transaction.paymentMethod ? t(transaction.paymentMethod) : '' },
    { label: t('contactName'), value: transaction.contactName },
    /*
     * The reference has no label of its own anywhere in the app - it is not a column on this
     * board - so it borrows the inquiry namespace rather than inventing a top-level key.
     */
    { label: t('inquiries.resource.reference'), value: transaction.donationReference ?? '' },
  ].filter((field) => !!field.value);

  return (
    <div className="flex items-center gap-[6px]">
      <div className={`flex items-center gap-[6px] ${isReceiptRelevant ? '' : 'invisible'}`} aria-hidden={!isReceiptRelevant}>
        <TableActionIcon title={t('downloadReceipt')} disabled={isReceiptMissing} onClick={() => onDownloadReceipt(transaction)}>
          <FileDownloadOutlinedIcon sx={{ fontSize: 22 }} />
        </TableActionIcon>
        <TableActionIcon title={t('sendReceiptByEmail')} disabled={isReceiptMissing} onClick={() => onSendReceiptEmail(transaction)}>
          <ForwardToInboxOutlinedIcon sx={{ fontSize: 22 }} />
        </TableActionIcon>
      </div>
      <OpenInquiryButton
        resourceType="Transaction"
        resourceId={String(transaction.transactionId)}
        resourceSummary={resourceSummary}
        size="medium"
      />
    </div>
  );
};

export default TransactionRowActions;
